import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { InstallationFeaturesStore, OnOffEnum, RemoteConfigStore } from '@store';

import {
  ActivateDeactivateCommands,
  DeviceFuelCurrentCommands,
  DeviceFuelMinimalCommands,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  HeatingServiceBurnerBasedProperties,
  IsActiveProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  ServiceBurnerBasedCommands,
  ServiceTimeBasedCommands,
  ServiceTimeBasedProperties,
} from '@vi-iot/client';

import { CustomTranslationsService } from '../../../../../operational-data-common/translations';
import { EditFeatureValueModalComponent } from '../../../../../shared/components/edit-feature-value-modal/edit-feature-value-modal.component';
import { EditFeatureValueData } from '../../../../../shared/models/editFeatureValueData.model';
import { SUFFIX } from '../../../../../shared/common/units';
import { GTMService } from '../../../../../shared/services';

@Component({
  selector: 'app-ecotronic-details',
  templateUrl: './ecotronic-details.component.html',
  styleUrls: ['./ecotronic-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EcotronicDetailsComponent implements OnInit, OnDestroy {
  SUFFIX = SUFFIX;
  FEATURE_NAMES = FeatureNames;
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly featureCommands = FeatureCommands;

  burnerManualActivationOptions: string[] = [
    this.customTranslationsService.ON_OFF_STATES.on,
    this.customTranslationsService.ON_OFF_STATES.off,
  ];
  editFeatureValueDialogRef: MatDialogRef<EditFeatureValueModalComponent>;
  editFuelMinimalValueModalData: EditFeatureValueData;
  editFuelCurrentValueModalData: EditFeatureValueData;
  editServiceBurnerBasedValueModalData: EditFeatureValueData;

  serviceTimeBased$: Observable<Feature<ServiceTimeBasedProperties, ServiceTimeBasedCommands>>;
  solarStatus$: Observable<Feature<IsStringValueProperty, never>>;
  heatingDeviceStatus$: Observable<Feature<IsStringValueProperty, never>>;
  configurationFuelCapacity$: Observable<Feature<IsNumberValueProperty, never>>;
  burnerManualActivation$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  heatingDeviceFuelMinimal$: Observable<Feature<IsNumberValueProperty, DeviceFuelMinimalCommands>>;
  heatingDeviceFuelCurrent$: Observable<Feature<IsNumberValueProperty, DeviceFuelCurrentCommands>>;
  serviceBurnerBased$: Observable<
    Feature<HeatingServiceBurnerBasedProperties, ServiceBurnerBasedCommands>
  >;

  heatingDeviceFuelMinimal: Feature<IsNumberValueProperty, DeviceFuelMinimalCommands>;
  heatingDeviceFuelCurrent: Feature<IsNumberValueProperty, DeviceFuelCurrentCommands>;
  serviceBurnerBased: Feature<HeatingServiceBurnerBasedProperties, ServiceBurnerBasedCommands>;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();
  private commonModalData = {
    title: 'FEATURE-VALUE-EDIT-MODAL.MODAL_TITLE',
    categoryNameForGTM: 'Ecotronic',
    errorText: {
      required: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.REQUIRED',
      min: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.MIN',
      max: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.MAX',
      pattern: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.NUMBERS_PATTERN',
    },
  };

  constructor(
    public remoteConfigStore: RemoteConfigStore,
    private dialog: MatDialog,
    private gtmService: GTMService,
    private cdr: ChangeDetectorRef,
    protected customTranslationsService: CustomTranslationsService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.serviceTimeBased$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.serviceTimeBased
    );
    this.solarStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.solarStatus
    );
    this.heatingDeviceStatus$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceStatus
    );
    this.configurationFuelCapacity$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationFuelCapacity
    );
    this.burnerManualActivation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.burnerManualActivation
    );
    this.heatingDeviceFuelMinimal$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceFuelMinimal
    );
    this.heatingDeviceFuelCurrent$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceFuelCurrent
    );
    this.serviceBurnerBased$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.serviceBurnerBased
    );

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsNumberValueProperty, DeviceFuelMinimalCommands>(
        FeatureNames.heatingDeviceFuelMinimal
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((heatingDeviceFuelMinimal) => {
        this.heatingDeviceFuelMinimal = heatingDeviceFuelMinimal;
        this.editFuelMinimalValueModalData = {
          ...this.commonModalData,
          value: heatingDeviceFuelMinimal?.properties?.value?.value,
          inputSettings: {
            name: '+INSTALLATIONS.INSTALLATION.SYSTEM_DETAILS.ECOTRONIC.MINIMAL_FILLING',
            actionNameForGTM: 'Minimal Filling',
            type: 'number',
            isRequired: true,
            min:
              heatingDeviceFuelMinimal?.commands?.setMinimalFilling?.params?.minimalFilling
                ?.constraints?.min,
            max:
              heatingDeviceFuelMinimal?.commands?.setMinimalFilling?.params?.minimalFilling
                ?.constraints?.max,
            pattern: '[0-9]+',
          },
          onSaveProperties: {
            featureName: FeatureNames.heatingDeviceFuelMinimal,
            propertyName: this.featurePropertyKey.minimalFilling,
            paramName: '',
            commandName: heatingDeviceFuelMinimal?.commands?.setMinimalFilling?.name,
          },
        };
        this.cdr.detectChanges();
      });

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsNumberValueProperty, DeviceFuelCurrentCommands>(
        FeatureNames.heatingDeviceFuelCurrent
      )
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((heatingDeviceFuelCurrent) => {
        this.heatingDeviceFuelCurrent = heatingDeviceFuelCurrent;
        this.editFuelCurrentValueModalData = {
          ...this.commonModalData,
          value: heatingDeviceFuelCurrent?.properties?.value?.value,
          inputSettings: {
            name: '+INSTALLATIONS.INSTALLATION.SYSTEM_DETAILS.ECOTRONIC.CURRENT_FUEL_TANK_VALUE',
            actionNameForGTM: 'Adjust Weight',
            type: 'number',
            isRequired: true,
            min:
              heatingDeviceFuelCurrent?.commands?.adjustTank?.params?.adjustWeight?.constraints
                ?.min,
            max:
              heatingDeviceFuelCurrent?.commands?.adjustTank?.params?.adjustWeight?.constraints
                ?.max,
            pattern: '[0-9]+',
          },
          onSaveProperties: {
            featureName: FeatureNames.heatingDeviceFuelCurrent,
            propertyName: this.featurePropertyKey.adjustWeight,
            paramName: '',
            commandName: heatingDeviceFuelCurrent?.commands?.adjustTank?.name,
          },
        };
        this.cdr.detectChanges();
      });

    this.installationFeaturesStore
      .selectInstallationDeviceFeature<
        HeatingServiceBurnerBasedProperties,
        ServiceBurnerBasedCommands
      >(FeatureNames.serviceBurnerBased)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((serviceBurnerBased) => {
        this.serviceBurnerBased = serviceBurnerBased;
        this.editServiceBurnerBasedValueModalData = {
          ...this.commonModalData,
          value: serviceBurnerBased?.properties?.serviceIntervalBurnerHours?.value,
          inputSettings: {
            name: '+INSTALLATIONS.INSTALLATION.SYSTEM_DETAILS.ECOTRONIC.HOUR',
            actionNameForGTM: 'Hour',
            type: 'number',
            isRequired: true,
            min: serviceBurnerBased?.commands?.setHourInterval?.params?.hour?.constraints?.min,
            max: serviceBurnerBased?.commands?.setHourInterval?.params?.hour?.constraints?.max,
            pattern: '[0-9]+',
          },
          onSaveProperties: {
            featureName: FeatureNames.serviceBurnerBased,
            propertyName: this.featurePropertyKey.hour,
            paramName: '',
            commandName: serviceBurnerBased?.commands?.setHourInterval?.name,
          },
        };
        this.cdr.detectChanges();
      });
  }

  openEditModal(featureName: string): void {
    let dialogData: EditFeatureValueData;

    if (featureName === FeatureNames.heatingDeviceFuelMinimal) {
      dialogData = this.editFuelMinimalValueModalData;
    } else if (featureName === FeatureNames.heatingDeviceFuelCurrent) {
      dialogData = this.editFuelCurrentValueModalData;
    } else if (featureName === FeatureNames.serviceBurnerBased) {
      dialogData = this.editServiceBurnerBasedValueModalData;
    }

    this.editFeatureValueDialogRef = this.dialog.open(EditFeatureValueModalComponent, {
      width: '496px',
      disableClose: true,
      data: dialogData,
    });
    this.gtmService.track(
      'onOpen',
      `Edit ${dialogData.inputSettings.actionNameForGTM} dialog`,
      this.commonModalData.categoryNameForGTM
    );
  }

  onBurnerManualActivationChange(value: string): void {
    if (value === OnOffEnum.On) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.burnerManualActivation,
        true,
        this.featurePropertyKey.active,
        '',
        FeatureCommands.activate
      );
      this.gtmService.track(
        'onToggle',
        'Burner manual activation',
        this.commonModalData.categoryNameForGTM
      );
    } else if (value === OnOffEnum.Off) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.burnerManualActivation,
        false,
        this.featurePropertyKey.active,
        '',
        FeatureCommands.deactivate
      );
      this.gtmService.track(
        'onToggle',
        'Burner manual deactivation',
        this.commonModalData.categoryNameForGTM
      );
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
